/**
 * Die Art eines eigenen Wortes. (23.09.2026)
 *
 * Martin: *„Das persönliche Vokabular in den Einstellungen ist so total
 * unbrauchbar, weil ich sehe, dass ich einfach nur eine ewig lange Liste
 * habe."* Im freigegebenen Entwurf stehen die Wörter nach Art sortiert als
 * Marken — Menschen, Orte, Vespera und Arbeit, Technik, Körper und Alltag.
 *
 * Neue Wörter ordnet Vespera selbst ein: ein Kontakt wird „Mensch", und ein
 * Vorschlag bringt seinen Grund schon mit („Nachname", „Ort",
 * „Medikament"). Was sich so nicht zuordnen lässt, bleibt OHNE Art — lieber
 * einmal selbst tippen als falsch einsortiert.
 *
 * Geteilt, weil Server (Einordnen) und Oberfläche (Gruppen, Blatt) dieselben
 * fünf Namen brauchen; zwei Listen liefen auseinander.
 */

export const VOKABEL_ARTEN = ["mensch", "ort", "arbeit", "technik", "alltag"] as const;
export type VokabelArt = (typeof VOKABEL_ARTEN)[number];

/** Überschrift der Gruppe (Mehrzahl, wie im Entwurf). */
export const ART_GRUPPE: Record<VokabelArt, string> = {
  mensch: "Menschen",
  ort: "Orte",
  arbeit: "Vespera und Arbeit",
  technik: "Technik",
  alltag: "Körper und Alltag",
};

/** Name im Blatt eines Wortes (Einzahl). */
export const ART_NAME: Record<VokabelArt, string> = {
  mensch: "Mensch",
  ort: "Ort",
  arbeit: "Vespera und Arbeit",
  technik: "Technik",
  alltag: "Körper und Alltag",
};

export function istVokabelArt(x: unknown): x is VokabelArt {
  return typeof x === "string" && (VOKABEL_ARTEN as readonly string[]).includes(x);
}

/*
 * ⚠️ Ganze Wörter vergleichen, keine Teilstücke: „ort" steckt in „Sport"
 *    und „Wort", „name" in „Firmenname". Und kein `\b` — das kennt keine
 *    Umlaute (21.09., sieben tote Regeln auf einmal).
 *
 * ⚠️ Die Reihenfolge zählt: „Ortsname" und „Firmenname" enden auf „name",
 *    gehören aber nicht zu den Menschen. Deshalb fragen Ort und Technik
 *    zuerst, die Menschen mit ihrer Endung danach.
 */
const REGELN: ReadonlyArray<readonly [VokabelArt, RegExp]> = [
  [
    "ort",
    /^(ort|orte|ortsname|ortsteil|stadt|land|region|gemeinde|dorf|insel|berg|see|fluss|gegend|bezirk|strasse|straße|reiseziel|urlaubsort|landschaft|hof|gasthof|lokal)$/,
  ],
  [
    "alltag",
    /^(medikament|wirkstoff|körper|koerper|körperteil|koerperteil|gesundheit|sport|sportart|übung|uebung|essen|gericht|speise|getränk|getraenk|spiel|kartenspiel|hobby|chakra)$/,
  ],
  [
    "technik",
    /^(technik|software|app|programm|firma|firmenname|marke|markenname|produkt|produktname|dienst|plattform|modell|abkürzung|abkuerzung|befehl|fachbegriff)$/,
  ],
  ["mensch", /^(name|nachname|vorname|spitzname|person|mensch|freund|freundin|kollege|kollegin|kunde|kundin|coach|trainer|trainerin)$/],
  ["arbeit", /^(projekt|kurs|seminar|veranstaltung|angebot|workshop|retreat|camp|arbeit|beruf|funktion)$/],
];

/** Aus dem Grund eines Vorschlags, z. B. „Nachname" oder „Ort in Marokko". */
export function artAusGrund(grund: string): VokabelArt | null {
  const woerter = grund
    .toLowerCase()
    .split(/[^a-zäöüß]+/)
    .filter(Boolean);
  for (const [art, muster] of REGELN) {
    if (woerter.some((w) => muster.test(w))) return art;
  }
  return null;
}

/**
 * Ein neues Wort einordnen: steht es in einem Namen aus den Kontakten, ist es
 * ein Mensch; sonst entscheidet der Grund; sonst bleibt es offen.
 */
export function artVermuten(
  wort: string,
  kontext: { namen?: readonly string[]; grund?: string },
): VokabelArt | null {
  const w = wort.trim().toLowerCase();
  if (w) {
    for (const name of kontext.namen ?? []) {
      if (name.toLowerCase().split(/\s+/).includes(w)) return "mensch";
    }
  }
  return kontext.grund ? artAusGrund(kontext.grund) : null;
}
